import { P2GAlert, P2GLink, P2GText, SvgWarningTriangle } from '@parcel2go/core-ui';
import { SidePanelContent } from '~/components/sidepanel-section';
import { FbmShipPlusReason, FbmShipPlusSellerCentralUrl } from '~/apps/smartsend/lib/helpers/SmartSendSummaryGenerator';

interface props {
  references: string[];
  // Meta value naming why dispatch is blocked. Undefined when blocked items disagree
  // on the reason, or when it is one we have no specific copy for.
  reason?: string;
}

const DispatchBlockedWarning = ({ references, reason }: props) => {
  const subject = references.length === 1 ? 'One of these orders is' : `${references.length} of these orders are`;
  const title =
    reason === FbmShipPlusReason
      ? 'You’ll need to dispatch these in Amazon'
      : 'You’ll need to dispatch these in the marketplace';

  return (
    <P2GAlert type="info" icon={<SvgWarningTriangle />} data-testid="dispatch-blocked-warning">
      <SidePanelContent>
        <P2GText variant="h5" component="h3">
          {title}
        </P2GText>
        {reason === FbmShipPlusReason ? (
          <>
            <P2GText>
              {subject} on Amazon’s <strong>FBM Ship+</strong> programme. Amazon won’t allow us to mark these as
              dispatched or update tracking numbers while the shipping templates these orders are on are set to FBM
              Ship+.
            </P2GText>
            <P2GText>
              <strong>You can still book and send these parcels as normal.</strong> You’ll just need to mark those
              orders as dispatched yourself in Seller Central.
            </P2GText>
            <P2GText variant="subtitle2" component="h4">
              How do I re-enable tracking updates?
            </P2GText>
            <P2GText>
              To send tracking automatically in future, remove the shipping templates these orders are on from FBM Ship+
              in Amazon Seller Central: <strong>FBM Ship+</strong> → <strong>Edit FBM Ship+ preferences</strong> →{' '}
              <strong>Edit template selection</strong>, then save.
            </P2GText>
          </>
        ) : (
          <>
            <P2GText>
              {subject} on a marketplace programme that will not accept tracking sent from outside the marketplace.
            </P2GText>
            <P2GText>
              <strong>You can still book and send these parcels as normal.</strong> We just will not be able to upload
              the tracking or mark them as dispatched, so you will need to confirm those orders yourself in the
              marketplace.
            </P2GText>
          </>
        )}
        {references.length > 0 && (
          <P2GText variant="body2" small component="p" data-testid="dispatch-blocked-references">
            Affected orders: {references.join(', ')}
          </P2GText>
        )}
        {reason === FbmShipPlusReason && (
          <P2GText variant="body2" small component="p">
            <P2GLink href={FbmShipPlusSellerCentralUrl} target="_blank" rel="noopener noreferrer">
              Read Amazon’s FBM Ship+ guidance
            </P2GLink>
          </P2GText>
        )}
      </SidePanelContent>
    </P2GAlert>
  );
};
export default DispatchBlockedWarning;
